<script setup lang="ts">
  import { ref } from 'vue';

  import type { EmailSender } from '@stuut/api/models';
  import {
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';
  import type { Editor } from '@ui/components/text-editor';
  import { TextEditor } from '@ui/components/text-editor';
  import { cn } from '@ui/utils/utils';

  import { stuut } from '@/api';

  interface Props {
    emailSender: EmailSender;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const body = ref(props.emailSender.signature_html || undefined);

  const isSaving = ref(false);

  const focused = ref(false);

  const onEditorReady = (editor: Editor) => {
    editor.commands.focus('end');
  };

  const saveSignature = async () => {
    if (isSaving.value) return;

    isSaving.value = true;

    try {
      await stuut.emailSenders.update(
        props.emailSender.id,
        { data: { signature_html: body.value || null } },
        props.emailSender,
      );
      emit('close');
    } catch (err) {
      toast.error((err as any).clientMessage || 'An error occurred');
    } finally {
      isSaving.value = false;
    }
  };
</script>

<template>
  <AlertDialogContent @escape-key-down.stop>
    <AlertDialogHeader>
      <AlertDialogTitle class="space-x-4 text-xl">
        <i class="fa fa-signature text-2xl" />
        <span class="font-bold"> Email Signature </span>
      </AlertDialogTitle>
    </AlertDialogHeader>

    <AlertDialogDescription class="space-y-4">
      <TextEditor
        v-model="body"
        :class="cn('py-3 border px-3 h-52 overflow-hidden rounded transition-colors duration-200')"
        @focus="focused = true"
        @blur="focused = false"
        @ready="onEditorReady"
      />
    </AlertDialogDescription>

    <AlertDialogFooter>
      <Button :disabled="isSaving" variant="outline" @click.stop.prevent="emit('close')">
        Cancel</Button
      >

      <Button :loading="isSaving" @click.stop.prevent="saveSignature"> Save</Button>
    </AlertDialogFooter>
  </AlertDialogContent>
</template>
